import {blockIsVisible} from "@reside/forms";

import {t} from "../../../../atoms/i18n-provider";

/** Template field ids owned by the PCC Integration nav section, not General. */
const PCC_TEMPLATE_FIELD_REFERENCES = new Set([
  "pccSettings",
  "pccPopulatingFlow",
]);

/** Resolved template controls store reference as an object; raw template uses a string key. */
const getFormControlReferenceId = (node: {
  reference?: string | {id?: string};
}): string | null => {
  const reference = node.reference;

  if (typeof reference === "string") {
    return reference;
  }

  if (
    reference &&
    typeof reference === "object" &&
    typeof reference.id === "string"
  ) {
    return reference.id;
  }

  return null;
};

export const blockContainsPccTemplateFields = (block: unknown): boolean => {
  if (!block || typeof block !== "object") {
    return false;
  }

  const node = block as {
    type?: string;
    reference?: string | {id?: string};
    children?: unknown[];
  };

  if (node.type === "form:control") {
    const referenceId = getFormControlReferenceId(node);
    if (referenceId && PCC_TEMPLATE_FIELD_REFERENCES.has(referenceId)) {
      return true;
    }
  }

  return Array.isArray(node.children)
    ? node.children.some(blockContainsPccTemplateFields)
    : false;
};

export const partitionFacilitySettingsBlocks = <T extends unknown>(
  blocks: ReadonlyArray<T>,
): {generalBlocks: T[]; pccTemplateBlocks: T[]} => {
  const pccTemplateBlocks = blocks.filter(blockContainsPccTemplateFields);
  const generalBlocks = blocks.filter(
    block => !blockContainsPccTemplateFields(block),
  );

  return {generalBlocks, pccTemplateBlocks};
};

export const filterVisibleFacilitySettingsBlocks = <T>(
  blocks: ReadonlyArray<T>,
  answers: Record<string, unknown>,
): T[] => blocks.filter(block => blockIsVisible(answers, block as any));

export const getFacilitySettingsBlockLabel = (block: any): string => {
  const subtitle = block?.children?.find(
    (child: any) => child.type === "subtitle",
  );
  if (subtitle?.translationKey) {
    const translated = t(subtitle.translationKey);
    if (translated && translated.trim() && translated.trim() !== " ") {
      return translated;
    }
  }
  return String(block.id);
};

/**
 * Copies the blocks with every block disabled, so the fields render disabled with the lock icon
 * (inactive facilities can't be edited).
 */
export const disableBlocks = <T>(blocks: ReadonlyArray<T>): T[] =>
  blocks.map(block => {
    const node = block as {state?: object; children?: unknown[]};

    return {
      ...node,
      state: {...node.state, disabled: true},
      ...(Array.isArray(node.children)
        ? {children: disableBlocks(node.children)}
        : {}),
    } as T;
  });
